import {
  InMemoryWebStorage,
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';
import { principalFromToken, type Principal } from './principal.ts';

export type AuthConfig = {
  keycloakUrl: string;
  realm: string;
  clientId: string;
  redirectUri: string;
  silentRedirectUri: string;
  postLogoutRedirectUri: string;
};

const SILENT_SIGN_IN_TIMEOUT_S = 5;

export class InsecureContextError extends Error {
  constructor(readonly origin: string) {
    super(`sign-in requires a secure context, origin ${origin}`);
    this.name = 'InsecureContextError';
  }
}

export const isSecureContextAvailable = (): boolean => typeof globalThis.crypto.subtle !== 'undefined';

const assertSecureContext = (): void => {
  if (!isSecureContextAvailable()) throw new InsecureContextError(globalThis.location.origin);
};

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'expired';

export type AuthState = { status: AuthStatus; principal: Principal | null };

const EXPIRED: AuthState = { status: 'expired', principal: null };

export type UserManagerLike = {
  getUser: () => Promise<User | null>;
  signinRedirect: (args?: { extraQueryParams?: Record<string, string>; state?: unknown }) => Promise<void>;
  signinCallback: (url?: string) => Promise<User | undefined>;
  signinSilent: () => Promise<User | null>;
  signoutRedirect: () => Promise<void>;
  events: {
    addUserLoaded: (callback: (user: User) => void) => void;
    addUserUnloaded: (callback: () => void) => void;
    addAccessTokenExpired: (callback: () => void) => void;
  };
};

export type LoginOptions = { organization?: string; returnTo?: string; locale?: string };

const loginQueryParams = (options: LoginOptions): Record<string, string> => ({
  ...(options.organization ? { organization: options.organization } : {}),
  ...(options.locale ? { ui_locales: options.locale } : {}),
});

export type AuthClient = {
  initialize: () => Promise<AuthState>;
  login: (options?: LoginOptions) => Promise<void>;
  handleCallback: () => Promise<{ returnTo: string }>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  markExpired: () => void;
  getState: () => AuthState;
  subscribe: (listener: (state: AuthState) => void) => () => void;
};

const redirectStateStorage = (): Storage =>
  typeof globalThis.sessionStorage === 'undefined' ? new InMemoryWebStorage() : globalThis.sessionStorage;

export const buildUserManagerSettings = (config: AuthConfig): UserManagerSettings => ({
  authority: `${config.keycloakUrl}/realms/${config.realm}`,
  client_id: config.clientId,
  redirect_uri: config.redirectUri,
  silent_redirect_uri: config.silentRedirectUri,
  silentRequestTimeoutInSeconds: SILENT_SIGN_IN_TIMEOUT_S,
  post_logout_redirect_uri: config.postLogoutRedirectUri,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  stateStore: new WebStorageStateStore({ store: redirectStateStorage() }),
});

const stateFromUser = (user: User | null | undefined, clientId: string): AuthState =>
  user && !user.expired && user.access_token
    ? { status: 'authenticated', principal: principalFromToken(user.access_token, clientId) }
    : { status: 'anonymous', principal: null };

export const createAuthClient = (
  config: AuthConfig,
  userManagerFactory?: (settings: UserManagerSettings) => UserManagerLike,
): AuthClient => {
  const settings = buildUserManagerSettings(config);
  const manager: UserManagerLike = userManagerFactory
    ? userManagerFactory(settings)
    : new UserManager(settings);
  let state: AuthState = { status: 'loading', principal: null };
  const listeners = new Set<(state: AuthState) => void>();

  let pendingCallback: Promise<{ returnTo: string }> | null = null;

  const publish = (next: AuthState) => {
    state = next;
    for (const listener of listeners) listener(state);
  };

  manager.events.addUserLoaded((user) => {
    publish(stateFromUser(user, config.clientId));
  });
  manager.events.addUserUnloaded(() => {
    publish({ status: 'anonymous', principal: null });
  });
  const renewedOrExpired = (user: User | null): AuthState => {
    const renewed = stateFromUser(user, config.clientId);
    return renewed.status === 'authenticated' ? renewed : EXPIRED;
  };

  manager.events.addAccessTokenExpired(() => {
    void manager.signinSilent().then(
      (user) => {
        publish(renewedOrExpired(user));
      },
      () => {
        publish(EXPIRED);
      },
    );
  });

  const existingSession = async (): Promise<AuthState> => {
    if (!isSecureContextAvailable()) return { status: 'anonymous', principal: null };
    return manager.signinSilent().then(
      (user) => stateFromUser(user, config.clientId),
      (): AuthState => ({ status: 'anonymous', principal: null }),
    );
  };

  const exchangeCodeOnce = async (): Promise<{ returnTo: string }> => {
    const user = await manager.signinCallback();
    publish(stateFromUser(user ?? null, config.clientId));
    const state = user?.state;
    const returnTo =
      typeof state === 'object' && state !== null && 'returnTo' in state ? String(state.returnTo) : '/';
    return { returnTo };
  };

  return {
    initialize: async () => {
      const stored = stateFromUser(await manager.getUser(), config.clientId);
      publish(stored.status === 'authenticated' ? stored : await existingSession());
      return state;
    },
    login: async (options = {}) => {
      assertSecureContext();
      const extraQueryParams = loginQueryParams(options);
      await manager.signinRedirect({
        ...(Object.keys(extraQueryParams).length > 0 ? { extraQueryParams } : {}),
        state: { returnTo: options.returnTo ?? '/' },
      });
    },
    handleCallback: () => {
      pendingCallback ??= exchangeCodeOnce();
      return pendingCallback;
    },
    logout: async () => {
      publish({ status: 'anonymous', principal: null });
      await manager.signoutRedirect();
    },
    getAccessToken: async () => {
      const user = await manager.getUser();
      return user && !user.expired ? user.access_token : null;
    },
    markExpired: () => {
      if (state.status === 'authenticated') publish(EXPIRED);
    },
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};
